import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useWorkspaceDetails } from "@project-mgmt/feature-workspaces";
import { useProject } from "@project-mgmt/feature-projects";
import { useTask } from "@project-mgmt/feature-tasks";
import { RBACService } from "@project-mgmt/feature-workspaces";

/**
 * Simple permission hooks that use existing RBACService
 * These hooks provide a clean interface for checking permissions without complex abstractions
 */

/**
 * Hook for workspace-level permissions
 */
export function useWorkspacePermissions() {
  const { workspaceId } = useParams();
  const { data } = useWorkspaceDetails(workspaceId ?? "");
  const workspace = data?.workspace;

  return useMemo(() => {
    if (!workspace) {
      return {
        canCreateProject: false,
        canManageWorkspace: false,
        canInviteUsers: false,
        canDeleteWorkspace: false,
        canViewMembers: false,
        canManageMembers: false,
        canViewProjects: false,
        canManageProjects: false,
      };
    }

    return {
      canCreateProject: RBACService.canCreateProjects(workspace),
      canManageWorkspace: RBACService.canManageWorkspace(workspace),
      canInviteUsers: RBACService.canInviteUsers(workspace),
      canDeleteWorkspace: RBACService.canDeleteWorkspace(workspace),
      canViewMembers: RBACService.canViewMembers(workspace),
      canManageMembers: RBACService.canManageMembers(workspace),
      canViewProjects: RBACService.canViewProjects(workspace),
      canManageProjects: RBACService.canManageProjects(workspace),
    };
  }, [workspace]);
}

/**
 * Hook for project-level permissions
 */
export function useProjectPermissions() {
  const { workspaceId, projectId } = useParams();
  const { data: workspaceData } = useWorkspaceDetails(workspaceId ?? "");
  const { data: projectData } = useProject(projectId ?? "", workspaceId ?? "");
  
  const workspace = workspaceData?.workspace;
  const project = projectData;

  return useMemo(() => {
    if (!workspace || !project) {
      return {
        canCreateTask: false,
        canManageProject: false,
        canViewTasks: false,
        canManageTasks: false,
        canUploadFiles: false,
        canDeleteFiles: false,
      };
    }

    return {
      canCreateTask: RBACService.canCreateTasks(workspace),
      canManageProject: RBACService.canManageProjects(workspace),
      canViewTasks: RBACService.canViewProjects(workspace), // Using canViewProjects as proxy
      canManageTasks: RBACService.canManageTasks(workspace),
      canUploadFiles: RBACService.canUploadFiles(workspace),
      canDeleteFiles: RBACService.canDeleteFiles(workspace),
    };
  }, [workspace, project]);
}

/**
 * Hook for task-level permissions
 */
export function useTaskPermissions() {
  const { workspaceId, projectId, taskId } = useParams();
  const { data: workspaceData } = useWorkspaceDetails(workspaceId ?? "");
  const { data: projectData } = useProject(projectId ?? "", workspaceId ?? "");
  const { data: taskData } = useTask(taskId ?? "", workspaceId ?? "");
  
  const workspace = workspaceData?.workspace;
  const project = projectData;
  const task = taskData;

  return useMemo(() => {
    if (!workspace || !project || !task) {
      return {
        canManageTask: false,
        canUpdateTaskStatus: false,
        canAssignTask: false,
        canUploadFiles: false,
        canDeleteFiles: false,
      };
    }

    return {
      canManageTask: RBACService.canManageTasks(workspace),
      canUpdateTaskStatus: RBACService.canUpdateTaskStatus(workspace),
      canAssignTask: RBACService.canAssignTasks(workspace),
      canUploadFiles: RBACService.canUploadFiles(workspace),
      canDeleteFiles: RBACService.canDeleteFiles(workspace),
    };
  }, [workspace, project, task]);
}

/**
 * Hook for file-level permissions
 */
export function useFilePermissions() {
  const { workspaceId, projectId, taskId } = useParams();
  const { data: workspaceData } = useWorkspaceDetails(workspaceId ?? "");
  const { data: projectData } = useProject(projectId ?? "", workspaceId ?? "");
  const { data: taskData } = useTask(taskId ?? "", workspaceId ?? "");
  
  const workspace = workspaceData?.workspace;
  const project = projectData;
  const task = taskData;

  return useMemo(() => {
    if (!workspace || !project || !task) {
      return {
        canUploadFiles: false,
        canDeleteFiles: false,
        canViewFiles: false,
      };
    }

    return {
      canUploadFiles: RBACService.canUploadFiles(workspace),
      canDeleteFiles: RBACService.canDeleteFiles(workspace),
      canViewFiles: !!workspace, // All workspace members can view files
    };
  }, [workspace, project, task]);
}

/**
 * Hook to check a specific permission
 * Useful for conditional rendering of UI elements
 */
export function useHasPermission(level: "workspace" | "project" | "task" | "file", permission: string): boolean {
  const workspacePermissions = useWorkspacePermissions();
  const projectPermissions = useProjectPermissions();
  const taskPermissions = useTaskPermissions();
  const filePermissions = useFilePermissions();

  return useMemo(() => {
    switch (level) {
      case "workspace":
        return (workspacePermissions as any)[permission] || false;
      case "project":
        return (projectPermissions as any)[permission] || false;
      case "task":
        return (taskPermissions as any)[permission] || false;
      case "file":
        return (filePermissions as any)[permission] || false;
      default:
        return false;
    }
  }, [level, permission, workspacePermissions, projectPermissions, taskPermissions, filePermissions]);
}

/**
 * Hook to get all permissions for the current context
 * Useful for debugging or comprehensive permission checks
 */
export function useAllPermissions() {
  const workspacePermissions = useWorkspacePermissions();
  const projectPermissions = useProjectPermissions();
  const taskPermissions = useTaskPermissions();
  const filePermissions = useFilePermissions();

  return {
    workspace: workspacePermissions,
    project: projectPermissions,
    task: taskPermissions,
    file: filePermissions,
  };
}
